/** @type {string[]} */
const KEYBINDING_MODIFIER_KEYS = ['Shift', 'Meta', 'Alt', 'Control'];

/** @type {number} */
const DEFAULT_TIMEOUT = 1000;

/** @type {string} */
const DEFAULT_EVENT = 'keydown';

/** @type {string} */
const PLATFORM = typeof navigator === 'object' ? navigator.platform : '';

/** @type {boolean} */
const APPLE_DEVICE = /Mac|iPod|iPhone|iPad/.test(PLATFORM);

/** @type {string} */
const MOD = APPLE_DEVICE ? 'Meta' : 'Control';

/** @type {string[]} */
const ALT_GRAPH_ALIASES = PLATFORM === 'Win32' ? ['Control', 'Alt'] : APPLE_DEVICE ? ['Alt'] : [];

/**
 * Gets the modifier state for a keyboard event.
 * @param {KeyboardEvent} event - The keyboard event
 * @param {string} mod - The modifier key to check
 * @returns {boolean} Whether the modifier is active
 */
const getModifierState = (event, mod) => {
  return typeof event.getModifierState === 'function'
    ? event.getModifierState(mod) ||
        (ALT_GRAPH_ALIASES.includes(mod) && event.getModifierState('AltGraph'))
    : false;
};

/**
 * Parses a key binding string into its parts.
 * @param {string} str - The key binding string to parse
 * @returns {Array<[string[], string|RegExp]>} Array of [modifiers, key] pairs
 */
const parseKeybinding = (str) => {
  return str
    .trim()
    .split(' ')
    .map((press) => {
      const mods = press.split(/\b\+/);
      let key = mods.pop();
      const match = key.match(/^\((.+)\)$/);
      if (match) {
        key = new RegExp(`^${match[1]}$`);
      }
      const processedMods = mods.map((mod) => (mod === '$mod' ? MOD : mod));
      return [processedMods, key];
    });
};

/**
 * Checks if a keyboard event matches a keybinding press.
 * @param {KeyboardEvent} event - The keyboard event to check
 * @param {[string[], string|RegExp]} bindingPress - Array containing [modifiers, key]
 * @returns {boolean} Whether the event matches the binding press
 */
const matchKeyBindingPress = (event, [mods, key]) => {
  return !(
    (key instanceof RegExp
      ? !(key.test(event.key) || key.test(event.code))
      : key.toUpperCase() !== event.key.toUpperCase() && key !== event.code) ||
    mods.find((mod) => {
      return !getModifierState(event, mod);
    }) ||
    KEYBINDING_MODIFIER_KEYS.find((mod) => {
      return !mods.includes(mod) && key !== mod && getModifierState(event, mod);
    })
  );
};

/**
 * Creates an event listener for handling keybindings.
 * @param {Object<string, Function>} keyBindingMap - Map of key bindings to callbacks
 * @param {Object} [options={}] - Configuration options
 * @param {number} [options.timeout] - Timeout for key sequences
 * @returns {Function} Event handler function
 * @example
 * ```js
 * import { createKeybindingsHandler } from "../src/keybindings"
 *
 * let handler = createKeybindingsHandler({
 * 	"Shift+d": () => {
 * 		alert("The 'Shift' and 'd' keys were pressed at the same time")
 * 	},
 * 	"y e e t": () => {
 * 		alert("The keys 'y', 'e', 'e', and 't' were pressed in order")
 * 	},
 * 	"$mod+d": () => {
 * 		alert("Either 'Control+d' or 'Meta+d' were pressed")
 * 	},
 * })
 *
 * window.addEvenListener("keydown", handler)
 * ```
 */
const createKeybindingsHandler = (keyBindingMap, options = {}) => {
  const timeout = options.timeout ?? DEFAULT_TIMEOUT;

  const keyBindings = Object.keys(keyBindingMap).map((key) => {
    return [parseKeybinding(key), keyBindingMap[key]];
  });

  const possibleMatches = new Map();
  let timer = null;

  return (event) => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    keyBindings.forEach((keyBinding) => {
      const sequence = keyBinding[0];
      const callback = keyBinding[1];

      const prev = possibleMatches.get(sequence);
      const remainingExpectedPresses = prev ? prev : sequence;
      const currentExpectedPress = remainingExpectedPresses[0];

      const matches = matchKeyBindingPress(event, currentExpectedPress);

      if (!matches) {
        if (!getModifierState(event, event.key)) {
          possibleMatches.delete(sequence);
        }
      } else if (remainingExpectedPresses.length > 1) {
        possibleMatches.set(sequence, remainingExpectedPresses.slice(1));
      } else {
        possibleMatches.delete(sequence);
        callback(event);
      }
    });

    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(possibleMatches.clear.bind(possibleMatches), timeout);
  };
};

/**
 * Subscribes to keybindings on a target element.
 * @param {EventTarget} target - The target element to bind events to
 * @param {Object<string, Function>} keyBindingMap - Map of key bindings to callbacks
 * @param {Object} [options={}] - Configuration options
 * @param {string} [options.event='keydown'] - Event type to listen for
 * @param {boolean} [options.capture] - Whether to use capture phase
 * @param {number} [options.timeout] - Timeout for key sequences
 * @returns {Function} Unsubscribe function
 * @example
 * ```js
 * import { shortCuts } from "../src/keys"
 *
 * shortCuts(window, {
 * 	"Shift+d": () => {
 * 		alert("The 'Shift' and 'd' keys were pressed at the same time")
 * 	},
 * 	"y e e t": () => {
 * 		alert("The keys 'y', 'e', 'e', and 't' were pressed in order")
 * 	},
 * 	"$mod+d": () => {
 * 		alert("Either 'Control+d' or 'Meta+d' were pressed")
 * 	},
 * })
 * ```
 */
const shortcuts = (target, keyBindingMap, { event = DEFAULT_EVENT, capture, timeout } = {}) => {
  const onKeyEvent = createKeybindingsHandler(keyBindingMap, { timeout });
  target.addEventListener(event, onKeyEvent, capture);
  return () => {
    target.removeEventListener(event, onKeyEvent, capture);
  };
};

/**
 * React hook for keyboard shortcuts.
 * @param {Object<string, Function>} keyBindingMap - Map of key bindings to callbacks
 * @param {Object} [options={}] - Configuration options
 * @param {EventTarget} [options.target=window] - Target element to bind events to
 * @param {string} [options.event='keydown'] - Event type to listen for
 * @param {boolean} [options.capture] - Whether to use capture phase
 * @param {number} [options.timeout] - Timeout for key sequences
 * @param {Array} [options.deps=[]] - Dependencies array for useEffect
 * @example
 * ```js
 * import { useKeyboardShortcuts } from "../src/keys"
 *
 * const MyComponent = () => {
 *   useKeyboardShortcuts({
 *     "Shift+d": () => console.log("Shift+d pressed"),
 *     "Ctrl+c": () => console.log("Copy shortcut"),
 *     "$mod+s": () => console.log("Save shortcut")
 *   });
 *
 *   return <div>Press keyboard shortcuts!</div>;
 * };
 * ```
 */
export const useKeyboardShortcuts = (
  keyBindingMap,
  { target = window, event = DEFAULT_EVENT, capture, timeout } = {},
) => {
  const { useEffect } = require('react');

  useEffect(() => {
    if (!keyBindingMap || Object.keys(keyBindingMap).length === 0) {
      return;
    }

    const unsubscribe = shortcuts(target, keyBindingMap, { event, capture, timeout });

    return () => {
      unsubscribe();
    };
  }, [keyBindingMap, target, event, capture, timeout]);
};
